import { CardContent, Divider, Link, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { FC, useState } from 'react';
import { useEventBus } from '../../../../../../hooks/use-event-bus';
import { getAge, useForms, useLegacyForms } from '../../../../../../hooks/use-forms-service';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { FormAssignmentDto, PatientDto } from '../../../../../../services/forms-service/forms-service-types';
import { PatientRp } from '../../../../../layout/slideout/patient-profile-widget/header/contact-info/contact-info.styles';
import {
  ActionButtons,
  Buttons,
  Completed,
  ConsentGiven,
  ConsentNeeded,
  Container,
  Menu,
  MenuItem,
  NotSubmitted,
  RightAlignedActionButtons,
  RightAlignedActionCell,
} from './forms.styles';

import CirclePlusIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailIcon from '@mui/icons-material/Email';
import UploadFileIcon from '@mui/icons-material/FileUpload';
import SmsIcon from '@mui/icons-material/Forum';
import PhoneIcon from '@mui/icons-material/PhoneIphone';
import QrCodeIcon from '@mui/icons-material/QrCode';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { EventFormsDialog } from '../../../../../../types/event-bus';
import { actDisplayModal } from '../../../../../layout/slideout/forms-widget/modal-select-patient';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { Button, Card, CardTitle, Table } from '../../patient-profile-page.styles';
import LoadingSkeleton from './forms-skeleton';

const renderConsent = (patient: PatientDto, publish: (event: any) => void, publishToIframes: (event: any) => void) => {
  if (patient.isResponsibleParty) {
    return <PatientRp>RP</PatientRp>;
  }

  const age = getAge(patient.dateOfBirth);
  if (age && age < 18) {
    return 'Minor';
  }

  if (!patient.consentGiven) {
    return (
      <ConsentNeeded
        onClick={() => {
          actDisplayModal({
            publish,
            publishToIframes,
            patient,
            actionType: 'consent'
          });
        }}
      >
        Consent Needed
      </ConsentNeeded>
    );
  } else {
    return <ConsentGiven>Consent Given</ConsentGiven>;
  }
};

const renderLegacyStatus = (status: string) => {
  const normalizedStatus = status.toLowerCase();
  const isCompleteStatus =
    normalizedStatus.includes('complete') ||
    normalizedStatus.includes('completed') ||
    normalizedStatus.includes('submitted') ||
    normalizedStatus.includes('signed');

  return isCompleteStatus ? <Completed>Complete</Completed> : <NotSubmitted>{status || 'Not Submitted'}</NotSubmitted>;
};

export const FormsView: FC = () => {
  const { publish, publishToIframes } = useEventBus();
  const { contactDetails, locationId } = usePatientProfile();

  const [sendFillAnchorEl, setSendFillAnchorEl] = useState<null | HTMLElement>(null);
  const sendFillOpen = Boolean(sendFillAnchorEl);

  const [addRemoveAnchorEl, setAddRemoveAnchorEl] = useState<null | HTMLElement>(null);
  const addRemoveOpen = Boolean(addRemoveAnchorEl);

  const [patientSelected, setPatientSelected] = useState<PatientDto | null>(null);

  const { forms, formsService, isLoading } = useForms(contactDetails?.id);
  const { legacyForms, isLegacyLoading } = useLegacyForms(contactDetails?.id, locationId);

  const downloadHandle = (form: FormAssignmentDto) => {
    formsService
      .getPdf({
        formId: form.submitted ? form.submissionId! : form.formId!,
        isSubmitted: !!form.submitted
      })
      .then(pdf => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(pdf);
        link.download = `${form.formName}.pdf`;

        link.click();

        URL.revokeObjectURL(link.href);
        link.remove();
      });
  };

  const unassignHandle = (form: FormAssignmentDto) => {
    publish({
      messageType: 'formsDialog',
      data: { type: 'unassign', formId: form.id!, formName: form.formName! }
    });
  };

  const onClick = (actionType: EventFormsDialog['data']['type'] | 'upload-form') => {
    return () => {
      setSendFillAnchorEl(null);
      setAddRemoveAnchorEl(null);

      actDisplayModal({
        publish,
        publishToIframes,
        patient: patientSelected!,
        actionType
      });
    };
  };

  if (!forms && isLoading) {
    return (
      <Container>
        <LoadingSkeleton />
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <CardContent>
          {forms?.patients.map((patient, index) => {
            const formAssignments = patient.formAssignments ?? [];
            const isLastPatient = index === (forms?.patients.length ?? 0) - 1;
            const showLegacyRows = isLastPatient && !isLegacyLoading && !!legacyForms?.length;
            const shouldShowTable = formAssignments.length > 0 || showLegacyRows;
            const shouldShowNoData =
              formAssignments.length === 0 &&
              (!isLastPatient || (!isLegacyLoading && !(legacyForms?.length ?? 0)));

            return (
              <div key={`patient-forms-${patient.id}`}>
                <CardTitle>
                  <span>
                    {patient.firstName} {patient.lastName}, {getAge(patient.dateOfBirth)}
                    {renderConsent(patient, publish, publishToIframes)}
                  </span>
                  <Buttons className="header">
                    <Button
                      onClick={(e) => {
                        setPatientSelected(patient);
                        setSendFillAnchorEl(e.currentTarget);
                      }}
                    >
                      Send/Fill
                      <KeyboardArrowDownIcon />
                    </Button>
                    <Button
                      onClick={(e) => {
                        setPatientSelected(patient);
                        setAddRemoveAnchorEl(e.currentTarget);
                      }}
                    >
                      Add/Remove
                      <KeyboardArrowDownIcon />
                    </Button>
                  </Buttons>
                </CardTitle>
                {shouldShowNoData && <NoDataComponent />}
                {shouldShowTable && (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '30%' }}>Name</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>PMS ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell style={{ width: 0 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formAssignments.map((form) => (
                      <TableRow key={`form-${patient.id}-${form.id}`}>
                        <TableCell style={{ width: '30%' }}>{form.formName}</TableCell>
                        <TableCell>{form.submittedString}</TableCell>
                        <TableCell>{form.formType}</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell>
                          {form.submitted ? <Completed>Complete</Completed> : <NotSubmitted>Not Submitted</NotSubmitted>}
                        </TableCell>
                        <TableCell style={{ width: 0 }}>
                          <ActionButtons>
                            <Link component="button" variant="body2" onClick={() => unassignHandle(form)}>
                              Unassign
                            </Link>
                            <Link component="button" variant="body2" onClick={() => downloadHandle(form)}>
                              Download
                            </Link>
                          </ActionButtons>
                        </TableCell>
                      </TableRow>
                    ))}
                    {showLegacyRows && legacyForms!.map((form) => (
                      <TableRow key={`legacy-form-${form.id}`}>
                        <TableCell style={{ width: '30%' }}>{form.formName}</TableCell>
                        <TableCell>{form.date ? new Date(form.date).toLocaleDateString() : '—'}</TableCell>
                        <TableCell>Imported Submission</TableCell>
                        <TableCell>{form.pmsId ?? '—'}</TableCell>
                        <TableCell>{renderLegacyStatus(form.status)}</TableCell>
                        <RightAlignedActionCell>
                          <RightAlignedActionButtons>
                            <Link
                              component="a"
                              variant="body2"
                              href={formsService.getLegacyPdfUrl(form.id)}
                              target='_blank'
                              download={`${form.formName}.pdf`}
                            >
                              Download
                            </Link>
                          </RightAlignedActionButtons>
                        </RightAlignedActionCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
                {index !== (forms?.patients.length ?? 0) - 1 && <Divider sx={{ my: 2 }} />}
              </div>
            );
          })}
        </CardContent>
      </Card>
      <Menu anchorEl={sendFillAnchorEl} open={sendFillOpen} onClose={() => setSendFillAnchorEl(null)}>
        <MenuItem onClick={onClick('fill-on-this-device')}>
          <PhoneIcon />
          Fill on this device
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={onClick('send-email-link')}>
          <EmailIcon />
          Send Email Link
        </MenuItem>
        <MenuItem onClick={onClick('send-sms-link')}>
          <SmsIcon />
          Send Text Link
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={onClick('scan-qr-code')}>
          <QrCodeIcon />
          Scan QR Code
        </MenuItem>
      </Menu>
      <Menu anchorEl={addRemoveAnchorEl} open={addRemoveOpen} onClose={() => setAddRemoveAnchorEl(null)}>
        <MenuItem onClick={onClick('add-form')}>
          <CirclePlusIcon />
          Add Form From Library
        </MenuItem>
        <MenuItem onClick={onClick('upload-form')}>
          <UploadFileIcon />
          Upload Document
        </MenuItem>
        <MenuItem
          onClick={onClick('remove-all-assigned-forms')}
          disabled={patientSelected?.formAssignments?.length === 0}
        >
          <DeleteOutlineIcon />
          Remove All Assigned Forms
        </MenuItem>
      </Menu>
    </Container>
  );
};
